import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TMDB_KEY } from "./config";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";

const TMDB_IMAGE = "https://image.tmdb.org/t/p/w500";
const HEARTS = 10;
const CATEGORIES = [
  "Aksiyon",
  "Dram",
  "Romantik",
  "Bilim Kurgu",
  "Korku",
  "Komedi",
  "Animasyon",
  "Diğer",
];
const SELECTED_BY_OPTIONS = ["Hilal", "-", "Ortak"];

function WatchWithHilalApp() {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("");
  const [selectedBy, setSelectedBy] = useState("");
  const [favorite, setFavorite] = useState(false);
  const [movies, setMovies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const filmsRef = collection(db, "films");

  useEffect(() => {
    async function fetchMovies() {
      const snapshot = await getDocs(filmsRef);
      const list = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title || "",
          poster: data.poster || null,
          year: data.year || "",
          overview: data.overview || "",
          note: data.note ?? "",
          watched: data.watched ?? false,
          ratingHilal: data.ratingHilal ?? 0,
          ratingOkan: data.ratingOkan ?? 0,
          category: data.category || "Diğer",
          selectedBy:
            data.selectedBy === "Okan" ? "-" : data.selectedBy || "Ortak",
          favorite: data.favorite ?? false,
          createdAt: data.createdAt || 0,
        };
      });
      list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setMovies(list);
    }
    fetchMovies();
  }, []);

  const buildMovieFromTmdb = (movieFromTmdb) => ({
    title: movieFromTmdb?.title || title.trim(),
    poster: movieFromTmdb?.poster_path
      ? `${TMDB_IMAGE}${movieFromTmdb.poster_path}`
      : null,
    year: movieFromTmdb?.release_date
      ? movieFromTmdb.release_date.slice(0, 4)
      : "",
    overview: movieFromTmdb?.overview || "",
    note: note.trim(),
    watched: false,
    ratingHilal: 0,
    ratingOkan: 0,
    category: category || "Diğer",
    selectedBy: selectedBy || "Ortak",
    favorite: favorite,
    createdAt: Date.now(),
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setIsAdding(true);
    try {
      const res = await fetch(
        `https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(
          title.trim()
        )}&language=tr-TR`
      );
      const data = await res.json();
      if (data.results?.length === 0) {
        await addFromTmdb(null);
      } else if (data.results.length === 1) {
        await addFromTmdb(data.results[0]);
      } else {
        setSearchResults(data.results.slice(0, 8));
      }
    } catch (err) {
      console.error("TMDB arama hatası:", err);
    }
    setIsAdding(false);
  };

  const addFromTmdb = async (movieFromTmdb) => {
    const newMovie = buildMovieFromTmdb(movieFromTmdb);
    const ref = await addDoc(filmsRef, newMovie);
    setMovies((prev) => [{ id: ref.id, ...newMovie }, ...prev]);
    resetForm();
  };

  const handleSelectMovie = async (movie) => {
    await addFromTmdb(movie);
    setSearchResults([]);
  };

  const resetForm = () => {
    setTitle("");
    setNote("");
    setCategory("");
    setSelectedBy("");
    setFavorite(false);
  };

  const updateMovie = async (id, updates) => {
    await updateDoc(doc(db, "films", id), updates);
    setMovies((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
    );
  };

  const deleteMovie = async (id) => {
    await deleteDoc(doc(db, "films", id));
    setMovies((prev) => prev.filter((m) => m.id !== id));
  };

  const toggleWatched = (m) =>
    updateMovie(m.id, { watched: !m.watched });

  const toggleFavorite = (m) =>
    updateMovie(m.id, { favorite: !m.favorite });

  const updateNote = (m, val) =>
    updateMovie(m.id, { note: val });

  const updateRating = (m, who, val) => {
    const newVal = m[who] === val ? 0 : val;
    updateMovie(m.id, { [who]: newVal });
  };

  const calcAvg = (m) => {
    const vals = [m.ratingHilal, m.ratingOkan].filter((x) => x > 0);
    return vals.length
      ? Math.round(
          (vals.reduce((a, b) => a + b, 0) / vals.length) * 10
        ) / 10
      : 0;
  };

  const renderHearts = (current, onSelect) => (
    <div className="flex gap-1 justify-center">
      {Array.from({ length: HEARTS }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(i + 1)}
          className="text-2xl md:text-3xl hover:scale-125 transition"
        >
          <span
            className={`${
              i + 1 <= current
                ? "text-red-500 drop-shadow-[0_0_10px_red]"
                : "text-gray-700 hover:text-red-400"
            }`}
          >
            ♥
          </span>
        </button>
      ))}
    </div>
  );

  const filteredMovies = movies;

  return (
    <div
      className="min-h-screen text-white px-4 py-8 bg-center bg-cover bg-fixed relative overflow-hidden"
      style={{
        backgroundImage:
          'linear-gradient(rgba(0,0,0,0.85), rgba(0,0,0,0.95)), url("https://thumbs.dreamstime.com/b/abandoned-theater-interior-dusty-stage-red-curtains-explore-abandoned-theater-showcasing-dusty-stage-red-366214916.jpg")',
      }}
    >
      {/* Sadece kar yağışı kaldı – hafif ve güzel */}
      <div className="snowflakes absolute inset-0 pointer-events-none z-10">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="snowflake absolute text-white animate-snowfall"
            style={{
              left: `${Math.random() * 100}vw`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 15}s`,
              fontSize: `${12 + Math.random() * 12}px`,
              opacity: 0.6 + Math.random() * 0.4,
            }}
          >
            ❄
          </div>
        ))}
      </div>

      {/* Başlık */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8 relative z-20"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold text-red-600 drop-shadow-lg">
          Bugün ne izlesek?
        </h1>
      </motion.div>

      {/* Arama Formu */}
      <motion.form
        onSubmit={handleSearch}
        className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-3 mb-8 relative z-20"
      >
        <input
          type="text"
          placeholder="Film adı (örn: The Dark Knight)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="flex-1 p-3 rounded-xl bg-gray-900/80 border border-gray-600/50"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="p-3 rounded-xl bg-gray-900/80 border border-gray-700"
        >
          <option value="">Tür (opsiyonel)</option>
          {CATEGORIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          value={selectedBy}
          onChange={(e) => setSelectedBy(e.target.value)}
          className="p-3 rounded-xl bg-gray-900/80 border border-gray-700"
        >
          <option value="">Kim seçti?</option>
          {SELECTED_BY_OPTIONS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <button
          type="submit"
          disabled={isAdding}
          className="px-6 py-3 bg-red-700 rounded-xl hover:bg-red-800 transition"
        >
          {isAdding ? "Aranıyor..." : "Film Ara"}
        </button>
      </motion.form>

      {/* Arama Sonuçları Popup */}
      <AnimatePresence>
        {searchResults.length > 0 && (
          <motion.div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50">
            <motion.div className="bg-gray-900 p-6 rounded-2xl max-w-4xl w-full">
              <h2 className="text-center text-xl font-bold mb-4">
                Doğru filmi seç 🎞️
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {searchResults.map((m) => (
                  <div
                    key={m.id}
                    className="cursor-pointer text-center"
                    onClick={() => handleSelectMovie(m)}
                  >
                    <img
                      src={
                        m.poster_path
                          ? `${TMDB_IMAGE}${m.poster_path}`
                          : "https://via.placeholder.com/200x300?text=No+Image"
                      }
                      alt={m.title}
                      className="rounded-lg"
                    />
                    <p className="text-xs mt-2">
                      {m.title} ({m.release_date?.slice(0, 4)})
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setSearchResults([])}
                className="w-full mt-6 py-2 bg-red-800 rounded-lg"
              >
                İptal
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Film Kartları */}
      <div className="max-w-7xl mx-auto grid gap-8 sm:grid-cols-2 lg:grid-cols-3 relative z-20">
        {filteredMovies.length === 0 ? (
          <p className="text-gray-500 text-center col-span-full mt-12 text-xl">
            Salon boş... Hiç film eklenmemiş.
          </p>
        ) : (
          filteredMovies.map((m) => {
            const avg = calcAvg(m);
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-black/60 border border-gray-700/50 rounded-2xl overflow-hidden shadow-2xl"
              >
                <div className="relative">
                  {m.poster ? (
                    <img
                      src={m.poster}
                      alt={m.title}
                      className="w-full h-96 object-cover"
                    />
                  ) : (
                    <div className="w-full h-96 bg-gray-800 flex items-center justify-center text-gray-500">
                      Poster yok
                    </div>
                  )}
                  <button
                    onClick={() => deleteMovie(m.id)}
                    className="absolute top-2 right-2 text-2xl w-10 h-10 bg-red-800/80 rounded-full flex items-center justify-center hover:bg-red-900"
                  >
                    ×
                  </button>
                  <div className="absolute top-2 left-2 flex flex-col gap-1 text-xs">
                    <span className="bg-black/70 px-3 py-1 rounded">{m.category}</span>
                    <span className="bg-black/70 px-3 py-1 rounded">{m.selectedBy}</span>
                    {m.favorite && (
                      <span className="bg-red-800 px-3 py-1 rounded">⭐ Favori</span>
                    )}
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h2 className="text-xl font-bold">
                      {m.title} <span className="text-gray-400 text-sm">({m.year})</span>
                    </h2>
                    <button
                      onClick={() => toggleWatched(m)}
                      className={`text-xs px-3 py-1 rounded ${m.watched ? 'bg-green-800/50' : 'bg-gray-700/50'}`}
                    >
                      {m.watched ? "İzlendi ✓" : "İzlenecek"}
                    </button>
                  </div>
                  <textarea
                    value={m.note}
                    onChange={(e) => updateNote(m, e.target.value)}
                    placeholder="Not ekle..."
                    className="w-full text-sm bg-black/40 p-3 rounded border border-gray-700 resize-none"
                    rows={3}
                  />
                  <div className="text-center text-lg text-yellow-300 font-bold">
                    Ortalama: {avg}/10
                  </div>
                  <div>
                    <span className="text-pink-400 text-sm block text-center mb-1">Hilal</span>
                    {renderHearts(m.ratingHilal, (v) => updateRating(m, "ratingHilal", v))}
                  </div>
                  <div>
                    <span className="text-blue-400 text-sm block text-center mb-1">-</span>
                    {renderHearts(m.ratingOkan, (v) => updateRating(m, "ratingOkan", v))}
                  </div>
                  <button
                    onClick={() => toggleFavorite(m)}
                    className={`w-full py-2 rounded text-sm font-medium transition ${
                      m.favorite
                        ? "bg-red-800 text-white"
                        : "bg-gray-800 text-yellow-400"
                    }`}
                  >
                    ⭐ {m.favorite ? "Favorilerden Çıkar" : "Favori Yap"}
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Kar animasyonu */}
      <style jsx global>{`
        @keyframes snowfall {
          0% { transform: translateY(-10vh) translateX(0); }
          100% { transform: translateY(110vh) translateX(50px); }
        }
        .snowflake {
          animation: snowfall linear infinite;
        }
      `}</style>
    </div>
  );
}

export default WatchWithHilalApp;